import { ChangeDetectorRef, Component, forwardRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CustomDesignItem, CustomDesignItemType } from '@iupics-components/models/custom-design';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { EditViewUtils } from '@iupics-components/standard/layouts/edit-view-ui/utils/edit-view.utils';
import { IupicsSpecificWindow } from '@iupics-manager/models/iupics-data';
import {
  ApizGridComponent,
  ApizGridHeadlessComponent,
  ContextMenuItemDef,
  DataSource,
  DataSourceGetDataParams,
  GetContextMenuItemsFn,
  GetContextMenuItemsParams,
  provideGridApiService,
  providePanelService,
} from '@iupics/apiz-grid';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import SpecificWindowUiComponent from '../specific-window-ui/specific-window-ui.component';

import { ChangelogsService } from '@compiere-ws/services/changelogs/changelogs.service';
import { FilterCtxService } from '@iupics-components/standard/grid/filters/services/filter-ctx.service';
import { ViewColumnsService } from '@iupics-components/standard/grid/services/view-columns.service';
import { AppConfig } from '@iupics-config/app.config.service';
import { DateUtils } from '@iupics-util/tools/date.utils';

@Component({
  selector: 'iu-changelog-window-ui',
  templateUrl: './changelog-window-ui.component.html',
  styleUrls: ['./changelog-window-ui.component.scss'],
  standalone: true,
  imports: [ButtonModule, TooltipModule, TranslateModule, ApizGridHeadlessComponent],
  providers: [provideGridApiService(), providePanelService(), forwardRef(() => FilterCtxService), ViewColumnsService],
})
export default class ChangelogWindowUIComponent extends SpecificWindowUiComponent implements OnInit, OnDestroy {
  @ViewChild('grid', { static: false }) grid: ApizGridComponent;
  tableDisplayed = false;
  isCopiedToClipBoard = false;
  cacheBlockSize: number = 10;
  auditsInfo = {
    created_by: '',
    updated_by: '',
    tableName: '',
    id_txt: '',
    columnsTableHeader: [],
  };
  headerCols;
  isLogged = false;
  totalLoaded = 0;
  getContextMenuItems: GetContextMenuItemsFn = this.#getContextMenuItems.bind(this);
  private dataSource: DataSource;
  protected config = inject(AppConfig);
  private customDesignItemGrid: CustomDesignItem = {
    vcr: 'vcrGrid',
    type: CustomDesignItemType.GRID,
    tableName: 'ChangeLog_Form_Table',
    cssClass: 'p-col-12',
    isLabelDisplay: false,
  };
  protected changelogsService = inject(ChangelogsService);
  #cd = inject(ChangeDetectorRef);

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.headerCols = [
      {
        field: 'columnName',
        headerName: this.translateService.instant('specificWindow.changeLog.headerCols.columnName'),
        enableRowGroup: true,
        editable: false,
        hide: false,
        width: 'min-content',
        resizable: true,
        sortable: false,
      },
      {
        field: 'newValue',
        headerName: this.translateService.instant('specificWindow.changeLog.headerCols.newValue'),
        enableRowGroup: true,
        editable: false,
        hide: false,
        width: 'min-content',
        resizable: true,
        sortable: false,
      },
      {
        field: 'oldValue',
        headerName: this.translateService.instant('specificWindow.changeLog.headerCols.oldValue'),
        enableRowGroup: true,
        editable: false,
        hide: false,
        width: 'min-content',
        resizable: true,
        sortable: false,
      },
      {
        field: 'updated',
        headerName: this.translateService.instant('specificWindow.changeLog.headerCols.updated'),
        enableRowGroup: true,
        editable: false,
        hide: false,
        width: 'min-content',
        resizable: true,
        sortable: false,
        valueFormatter: this.dateFormater.bind(this),
      },
      {
        field: 'updatedBy',
        headerName: this.translateService.instant('specificWindow.changeLog.headerCols.updatedBy'),
        enableRowGroup: true,
        editable: false,
        hide: false,
        width: 'min-content',
        resizable: true,
        sortable: false,
      },
    ];
  }

  /**
   * Je récupère les informations pour gérer la construction moi même
   * @param specific
   * @param isCssOnComponent
   */
  buildWindow(specific: IupicsSpecificWindow, isCssOnComponent = true) {
    this.parentFormID = specific.AD_FormDetail_ID;
    this.title = specific.title;
    this.specificData = specific;
    this.initAuditInfo();
  }

  dateFormater(value: Date | { value }) {
    if (value === null) {
      return '';
    }
    const valueToParse = value['value'] ? value['value'] : value;
    return DateUtils.formatLongLocaleStr(valueToParse);
  }

  private initAuditInfo() {
    this.tableDisplayed = false;
    if (this.parentComponent) {
      const editView = <EditViewUiComponent>this.parentComponent;
      this.auditsInfo.tableName = editView.data.tableName;
      if (!this.store.isWindowNewData(editView.currentDataStoreKey)) {
        this.subscriptions.push(
          this.changelogsService
            .getChangeLogInfo(editView.data.AD_Table_ID, editView.currentDataStoreKey.recordId)
            .subscribe((response) => {
              this.auditsInfo.updated_by = response.updatedBy + ' - ' + this.dateFormater(response.updated);
              this.auditsInfo.created_by = response.createdBy + ' - ' + this.dateFormater(response.created);
              this.auditsInfo.id_txt = '';
              this.isLogged = response.isLogged;
              const recordIds = editView.currentDataStoreKey.recordId.split(',');
              for (let i = 0; i < recordIds.length; i++) {
                if (this.auditsInfo.id_txt.length > 0) {
                  this.auditsInfo.id_txt += ' - ';
                }
                this.auditsInfo.id_txt += recordIds[i] + ':' + recordIds[++i];
              }
              if (this.isLogged) {
                this.dataSource = {
                  getData: (params: DataSourceGetDataParams) => {
                    return this.getRows(editView.data.AD_Table_ID, editView.currentDataStoreKey.recordId, params);
                  },
                };
                this.tableDisplayed = true;
                this.#cd.detectChanges();
                this.grid.api.setDatasource(this.dataSource);
              }

              document.body.style.cursor = 'auto';
            })
        );
      } else {
        document.body.style.cursor = 'auto';
      }
    }
  }

  getRows(tableId: number, recordId: string, params: DataSourceGetDataParams): void {
    this.subscriptions.push(
      this.changelogsService
        .getChangeLogs(tableId, recordId, params.request.startRow, params.request.endRow)
        .subscribe((response) => {
          if (params.request.startRow == 0) {
            this.totalLoaded = 0;
          } else {
            this.totalLoaded += response.length;
          }
          params.success({
            data: response,
            rowCount: response.length < params.request.endRow - params.request.startRow ? this.totalLoaded : -1,
          });
        })
    );
  }

  #getContextMenuItems(params: GetContextMenuItemsParams): ContextMenuItemDef[] {
    return [
      {
        name: this.translateService.instant(`gridTools.copy`),
        action: () => {
          EditViewUtils.copyToClipboard(params.value);
        },
        icon: '<span class="ag-icon ag-icon-copy" unselectable="on" role="presentation"></span>',
        type: 'item',
      },
    ];
  }

  copyToClipBoard(str: string) {
    str = str.replace(/\:/g, '=').replace(/-/g, 'AND');
    EditViewUtils.copyToClipboard(str);
    this.isCopiedToClipBoard = true;
    setTimeout(() => {
      this.isCopiedToClipBoard = false;
    }, 3000);
  }
}
